import { formatOrderTotal } from "./orderListHelpers";

/**
 * Pure helpers extracted from StorefrontOrderDetail.vue so they can be
 * unit-tested under vitest (which can't mount Vuetify 3 SFCs in this project).
 *
 * See also orderListHelpers.js — formatOrderDate/formatOrderTotal are reused
 * from there to keep formatting consistent across past-orders list and detail.
 */

/**
 * Format a per-item price for display.
 *
 * Reuses formatOrderTotal so all monetary surfaces on the shopper's order
 * pages render with identical "$XX.XX" semantics. The backend serializes
 * OrderItem.unit_price (Numeric(10,2)) as a JSON string in JSON:API output,
 * so this helper accepts numeric strings as well as numbers.
 *
 * @param {number|string|null|undefined} price
 * @returns {string} formatted "$XX.XX"
 */
export function formatItemPrice(price) {
  return formatOrderTotal(price);
}

/**
 * Compute and format the line subtotal for an order item.
 *
 * Line subtotal is unit price * quantity. Backend stores price as
 * Numeric(10,2) (string in JSON) and quantity as Integer; we coerce
 * defensively because frontend code occasionally receives string values
 * during loading transitions.
 *
 * Returns "$0.00" for malformed input rather than throwing — the UI shows
 * the row anyway, and the user can refresh if they see zeroes.
 *
 * @param {{price?: number|string, quantity?: number|string}|null|undefined} item
 * @returns {string} formatted "$XX.XX"
 */
export function computeLineSubtotal(item) {
  if (!item) return formatOrderTotal(0);
  const price = Number(item.price);
  const qty = Number(item.quantity);
  if (!Number.isFinite(price) || !Number.isFinite(qty)) {
    return formatOrderTotal(0);
  }
  if (qty <= 0) {
    return formatOrderTotal(0);
  }
  return formatOrderTotal(price * qty);
}

/**
 * Extract a human-readable shipping address from an order.
 *
 * Order.checkout_fields is a JSON column (api/src/models/order.py:29) whose
 * shape is driven by Campaign.checkout_fields. When a Shopper Location is
 * captured at checkout, it's stored under the "Shopper Location" key.
 *
 * Storage shape (today): a preformatted comma-separated string. Every
 * known writer flattens to a string:
 *   - StorefrontCart.vue (shopper checkout submit)
 *   - AddOrder.vue (admin-side order creation)
 *   - api/src/endpoints/campaign.py (report rendering already-flat)
 *
 * The structured-object branch below is defensive future-proofing — no
 * writer currently persists a structured object. If/when checkout
 * starts storing address fields individually, remove the string branch
 * and update the writers to emit the object shape.
 *
 * Returns a single-line string suitable for display, or null if no
 * address was captured. Caller decides whether to show a "no shipping
 * address" branch.
 *
 * @param {object|null|undefined} order - JSON:API normalized order
 * @returns {string|null}
 */
export function extractShippingAddress(order) {
  if (!order || typeof order !== "object") return null;

  // Guest (tokenized) payload path -- card tmMqkxM1.
  //
  // The guest endpoint returns an allowlisted projection built from the flat
  // ship_to_* columns, NOT checkout_fields (which it never emits). Before
  // this branch existed, a guest who opened their emailed order link fell
  // straight through to the checkout_fields lookup below, got null, and the
  // v-if="shippingAddress" block never rendered -- so they saw items and a
  // total but NO delivery address, on a page whose primary job is confirming
  // where the order is going. The backend's seven ship_to_* fields were dead
  // payload nothing read. Wire, do not remove: showing the address to the
  // token holder is PARITY with the logged-in view (same computed, same
  // component), and the confirmation email already reveals the order to
  // whoever holds the mailbox.
  //
  // Checked FIRST so the guest payload is authoritative on its own path; the
  // session path has no shipping_address key and is unchanged.
  const flat = order.shipping_address;
  if (typeof flat === "string") {
    const trimmedFlat = flat.trim();
    if (trimmedFlat.length > 0) return trimmedFlat;
  }

  const fields = order.checkout_fields;
  if (!fields || typeof fields !== "object") return null;
  const loc = fields["Shopper Location"];
  if (!loc) return null;

  // Legacy/preformatted path — pass through.
  if (typeof loc === "string") {
    const trimmed = loc.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof loc !== "object") return null;

  const street = [loc.street_and_number, loc.suite_unit_etc]
    .filter((s) => typeof s === "string" && s.trim().length > 0)
    .join(", ");
  const cityRegionZip = [loc.city, loc.region, loc.zip_code]
    .filter((s) => typeof s === "string" && s.trim().length > 0)
    .join(", ");
  const country =
    typeof loc.country === "string" && loc.country.trim().length > 0
      ? loc.country.trim()
      : "";

  const parts = [street, cityRegionZip, country].filter(
    (p) => p && p.length > 0,
  );
  if (parts.length === 0) return null;
  return parts.join(" — ");
}

/**
 * Defense-in-depth check that an order belongs to the current account.
 *
 * Backend /api/orders/<id> is already scoped by account_id for Shoppers via
 * RoleMixin._campaign_perms (api/src/models/mixins/roles.py, fix landed in
 * PR #964 / issue #963). This helper provides a second layer per CLAUDE.md
 * Principle 5 (defense in depth) so a future backend regression cannot
 * silently leak another shopper's order through this page.
 *
 * @param {object|null|undefined} order - JSON:API normalized order
 * @param {string|number|null|undefined} currentAccountId
 * @returns {boolean}
 */
export function isOwnOrder(order, currentAccountId) {
  if (!order || typeof order !== "object") return false;
  if (currentAccountId == null) return false;

  const orderAccountId =
    order?.account?._jv?.id ?? order?.account_id ?? null;
  if (orderAccountId == null) return false;

  return String(orderAccountId) === String(currentAccountId);
}

/**
 * Defense-in-depth check for the GUEST (tokenized) read path.
 *
 * isOwnOrder above is the second layer for the session path. The guest path
 * had none: it accepted whatever the backend returned, on precisely the
 * ANONYMOUS path, which inverts the stated intent of "a second layer so a
 * future backend regression cannot silently leak another shopper's order."
 *
 * A guest has no account, so account identity is not the check available.
 * ORDER identity is: the token is scoped to exactly one order, and the page
 * asked for a specific id, so the order that comes back must BE that order.
 * If the backend ever returns a different one, we refuse to render it rather
 * than trusting the backend's word alone.
 *
 * @param {object|null|undefined} order - the guest payload's order object
 * @param {string|number|null|undefined} requestedId - the id from the route
 * @returns {boolean}
 */
export function guestOrderMatchesRequest(order, requestedId) {
  if (!order || typeof order !== "object") return false;
  if (requestedId == null || String(requestedId).trim() === "") return false;
  const id = order.id;
  if (id == null) return false;
  return String(id) === String(requestedId).trim();
}

/**
 * Decide how to fetch an order: tokenized guest read, or session read.
 *
 * Card: https://trello.com/c/tmMqkxM1
 *
 * Guest checkout is the DEFAULT, so a confirmation email frequently reaches
 * someone with NO ACCOUNT who cannot sign in. Those links carry a signed,
 * expiring, single-order token in the query string; with one present we must
 * call the guest endpoint, which authorizes on the TOKEN and returns exactly
 * that one order.
 *
 * Without a token we keep the existing session-scoped path untouched, and
 * the token is never appended to it -- an authenticated shopper's request
 * must not carry a capability token it did not need.
 *
 * @param {string|number} orderId
 * @param {string|null|undefined} token - the `?token=` query param, if any
 * @returns {{isGuest: boolean, url: string, params?: object}}
 */
export function guestOrderRequest(orderId, token) {
  const trimmed = typeof token === "string" ? token.trim() : "";
  if (trimmed) {
    return {
      isGuest: true,
      url: `/guest-orders/${orderId}`,
      params: { token: trimmed },
    };
  }
  return { isGuest: false, url: `/orders/${orderId}` };
}
